import { getVariantSelectors } from '../../utils/variantsUtils'
import type { ContainerQueryToSelectorObj } from '../../shared.types'
import type { DomApplier } from '../cssFeatures.types'
import type { CssContext } from '../componentCss.types'
import type { CompVariantsViewItem } from '@wix/thunderbolt-catharsis'

// default params
const BG_SKEW_ANGLE = 20
const BG_ROTATE_ANGLE = 22
const BG_PAN_SPEED = 0.2
const IMAGE_PARALLAX_SPEED = 1.5

const PREVIEW_SELECTOR = '[data-preview-stage="true"]'

/* REMARK:
	We mimic `position: fixed` behavior with sticky positioning and negative margins for some of the effects
	to avoid `position: fixed` pitfalls (breaking inside transform or overflow).
	Firefox has a bug related to this
	https://bugzilla.mozilla.org/show_bug.cgi?id=1791232, which a well-placed `will-change: transform` solves.
   	Selector for Firefox is from https://browserstrangeness.bitbucket.io/css_hacks.html#firefox
*/
const MOZ_GLOBAL_RULE = {
	'@supports(-moz-appearance: none)': {
		body: {
			'--motion-bgreveal-will-change': 'transform',
		},
	},
}

const FULL_SCREEN_NO_FIXED_STYLE = { height: '100lvh', width: '100%', position: 'relative' }

const REVEAL_BG_MEDIA_STYLE = {
	'will-change': 'var(--motion-bgreveal-will-change)',
	height: 'calc(100% + 200lvh)',
	'margin-top': '-100lvh',
	// For Bg Reveal with media rendered as background-image some browsers need to force new stacking-context
	isolation: 'isolate',
}

const REVEAL_BG_IMG_STYLE = {
	position: 'sticky',
	height: 'max(100lvh, calc(100% - 200lvh))',
	width: '100%',
	top: '0px',
	'margin-bottom': 'calc(100lvh - max(100lvh, var(--motion-comp-height, 100%)))',
	display: 'block',
	padding: '0px',
}

function isFixedStyleEffect(name: string, params: any) {
	return (name === 'BgZoom' && params.direction === 'in') || ['BgReveal', 'BgParallax', 'BgFake3D'].includes(name)
}

const EFFECT_TO_INITIAL_SIZE_STYLING = {
	BgParallax: (params: any) => (params.applyFixedStyle ? REVEAL_BG_MEDIA_STYLE : FULL_SCREEN_NO_FIXED_STYLE),
	BgReveal: (params: any) => (params.applyFixedStyle ? REVEAL_BG_MEDIA_STYLE : FULL_SCREEN_NO_FIXED_STYLE),
	BgFake3D: (params: any) => (params.applyFixedStyle ? REVEAL_BG_MEDIA_STYLE : FULL_SCREEN_NO_FIXED_STYLE),
	BgSkew: (params: any) => ({
		'padding-bottom': `${(100 * Math.tan(((params.angle || BG_SKEW_ANGLE) * Math.PI) / 180)).toFixed(3)}%`,
		'margin-top': `${(-50 * Math.tan(((params.angle || BG_SKEW_ANGLE) * Math.PI) / 180)).toFixed(3)}%`,
	}),
	BgRotate: (params: any) => ({
		width: `max(100%, calc(${(100 * Math.cos(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(
			3
		)}cqw + ${(100 * Math.sin(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(3)}cqh))`,
		height: `max(100%, calc(${(100 * Math.sin(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(
			3
		)}cqw + ${(100 * Math.cos(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(3)}cqh))`,
		translate: 'calc(50cqw - 50%) calc(50cqh - 50%)',
	}),
	BgPan: (params: any) => ({
		position: 'relative',
		width: `${100 * (1 + (params.speed || BG_PAN_SPEED))}%`,
		left: `-${50 * (params.speed || BG_PAN_SPEED)}%`,
	}),
	BgZoom: (params: any) =>
		params.direction === 'in'
			? params.applyFixedStyle
				? REVEAL_BG_MEDIA_STYLE
				: FULL_SCREEN_NO_FIXED_STYLE
			: { position: 'relative', width: '115%', height: '115%', left: '-7.5%', top: '-7.5%' },
	ImageParallax: (params: any) => ({
		height: `${100 * (params.speed || IMAGE_PARALLAX_SPEED)}%`,
		position: 'relative',
	}),
}

const EFFECT_TO_INITIAL_WOW_STYLING = {
	BgParallax: (params: any) => (params.applyFixedStyle ? REVEAL_BG_IMG_STYLE : { height: '100%', width: '100%' }),
	BgReveal: (params: any) => (params.applyFixedStyle ? REVEAL_BG_IMG_STYLE : { height: '100%', width: '100%' }),
	BgFake3D: (params: any) => (params.applyFixedStyle ? REVEAL_BG_IMG_STYLE : { height: '100%', width: '100%' }),
	BgPan: (_params: any) => ({}),
	BgZoom: (params: any) =>
		params.direction === 'in'
			? params.applyFixedStyle
				? REVEAL_BG_IMG_STYLE
				: { height: '100%', width: '100%' }
			: {},
	ImageParallax: (_params: any) => ({ height: '100%', width: '100%', position: 'absolute' }),
}

function transformToFrameSelector(selectors: Array<string>, postFix: string) {
	return selectors.map((selector_) => `${selector_}${postFix}`).join(',')
}

function getBgRotateContainerRules(angle: number, bgMediaSelector: string) {
	const containerRules: ContainerQueryToSelectorObj = {}
	const angleInRad = (angle * Math.PI) / 180
	const tanAngle = Math.tan(angleInRad).toFixed(3)
	const cotAngle = Math.tan(((90 - angle) * Math.PI) / 180).toFixed(3)
	const minAspectRatioQuery = `@container(min-aspect-ratio: ${cotAngle})`
	const maxAspectRatioQuery = `@container(max-aspect-ratio: ${tanAngle})`
	const width = `calc(100% + min(${(100 * (Math.sqrt(1 + Math.pow(Math.tan(angleInRad), 2)) - 1)).toFixed(3)}%, ${(
		100 * Math.sin(angleInRad)
	).toFixed(3)}cqh))`
	const height = `calc(100% + min(${(100 * (Math.sqrt(1 + Math.pow(Math.tan(angleInRad), 2)) - 1)).toFixed(3)}%, ${(
		100 * Math.sin(angleInRad)
	).toFixed(3)}cqw))`
	containerRules[minAspectRatioQuery] = {}
	containerRules[maxAspectRatioQuery] = {}
	containerRules[minAspectRatioQuery][bgMediaSelector] = { width }
	containerRules[maxAspectRatioQuery][bgMediaSelector] = { height }
	return containerRules
}

function getStyleRulesByEffect(
	namedEffect: any,
	compId: string,
	{
		variantKey,
		variants,
		cssContext,
	}: {
		variantKey: string
		variants: CompVariantsViewItem | null
		cssContext: CssContext
	},
	isEditor: boolean
) {
	const styleRules: Record<string, {}> = {}
	const bgMediaSelector = transformToFrameSelector(
		getVariantSelectors(variantKey, variants, `${compId}`, cssContext),
		` [data-motion-part="BG_MEDIA ${compId}"]`
	)
	const wowImageSelector = transformToFrameSelector(
		getVariantSelectors(variantKey, variants, `${compId}`, cssContext),
		` [data-motion-part="BG_IMG ${compId}"]`
	)
	const bgLayersSelector = `${transformToFrameSelector(
		getVariantSelectors(variantKey, variants, `${compId}`, cssContext),
		` [data-motion-part="BG_LAYER ${compId}"]`
	)}`
	/* There is some static styling here that is done to support empty animations on breakpoints that override a default animation.
	   This could be solved in the future outside of catharsis, using static skins and style queries */
	if (!namedEffect || !namedEffect.type) {
		styleRules[bgLayersSelector] = { overflow: 'hidden' }
		styleRules[bgMediaSelector] = { height: '100%', width: '100%', margin: '0', overflow: 'hidden' }
		styleRules[wowImageSelector] = {
			height: '100%',
			width: '100%',
			margin: '0',
			overflow: 'hidden',
			position: 'absolute',
		}
		return styleRules
	}
	// in editor use non-fixed style by default and override only with stage-preview-selctor
	Object.assign(namedEffect, { applyFixedStyle: !isEditor })
	const name = namedEffect?.type as keyof typeof EFFECT_TO_INITIAL_SIZE_STYLING

	const sizeStyleMapper = EFFECT_TO_INITIAL_SIZE_STYLING[name]
	if (sizeStyleMapper) {
		styleRules[bgMediaSelector] = sizeStyleMapper(namedEffect)
	}
	const wowStyleMapper = EFFECT_TO_INITIAL_WOW_STYLING[name as keyof typeof EFFECT_TO_INITIAL_WOW_STYLING]
	if (wowStyleMapper) {
		styleRules[wowImageSelector] = wowStyleMapper(namedEffect)
	}
	styleRules[bgLayersSelector] = { overflow: 'clip' }

	if (name === 'BgRotate') {
		Object.assign(styleRules[bgLayersSelector], { 'container-type': 'size' })
		Object.assign(styleRules, getBgRotateContainerRules(namedEffect.angle || BG_ROTATE_ANGLE, bgMediaSelector))
	} else if (isFixedStyleEffect(name, namedEffect)) {
		Object.assign(styleRules, MOZ_GLOBAL_RULE)
		const bgMediaImageXSelector = transformToFrameSelector(
			getVariantSelectors(variantKey, variants, `${compId}`, cssContext),
			`[data-testid="imageX"] [data-motion-part="BG_MEDIA ${compId}"]`
		)
		// for ImageX in Safari, we experience a bug similar to Firefox, but in this case adding a transform does the trick
		styleRules[bgMediaImageXSelector] = {
			transform: 'translate3d(0,0,0)',
		}

		// rules that are toggled on stage-preview in editor for effects that use fixed style
		if (isEditor) {
			namedEffect.applyFixedStyle = true
			const bgMediaSelectorOnPreview = transformToFrameSelector(
				getVariantSelectors(variantKey, variants, `${compId}${PREVIEW_SELECTOR}`, cssContext),
				` [data-motion-part="BG_MEDIA ${compId}"]`
			)
			const wowImageSelectorOnPreview = transformToFrameSelector(
				getVariantSelectors(variantKey, variants, `${compId}${PREVIEW_SELECTOR}`, cssContext),
				` [data-motion-part="BG_IMG ${compId}"]`
			)
			if (sizeStyleMapper) {
				styleRules[bgMediaSelectorOnPreview] = sizeStyleMapper(namedEffect)
			}
			if (wowStyleMapper) {
				styleRules[wowImageSelectorOnPreview] = wowStyleMapper(namedEffect)
			}
		}
	}
	return styleRules
}

export const motionCssDomApplier: DomApplier<'motion'> = (
	{ id: compId },
	idSelector,
	breakpointId,
	{ motion },
	cssContext
) => {
	if (motion) {
		// fetches initial frame from all effects per a given breakpointId
		return motion
			.flatMap(({ data: breakpointToEffect, variants, componentViewMode }) => {
				const { default: defaultBreakpoint, ...restOfBreakpoints } = breakpointToEffect

				const defaultRules = Object.entries(defaultBreakpoint).map(([variantKey, item]) =>
					getStyleRulesByEffect(
						item?.namedEffect,
						compId,
						{ variantKey, variants, cssContext },
						componentViewMode === 'editor'
					)
				)
				if (breakpointId === 'default') {
					return defaultRules
				}

				return restOfBreakpoints[breakpointId]
					? Object.entries(restOfBreakpoints[breakpointId]).map(([variantKey, item]) =>
							getStyleRulesByEffect(
								item?.namedEffect,
								compId,
								{ variantKey, variants, cssContext },
								componentViewMode === 'editor'
							)
					  )
					: []
			})
			.reduce((acc, currentStyleRules) => {
				Object.keys(currentStyleRules).forEach((selector) => {
					if (!acc[selector]) {
						acc[selector] = {}
					}

					Object.assign(acc[selector], currentStyleRules[selector])
				})
				return acc
			}, {} as Record<string, {}>)
	}

	return null
}
